import React, { useCallback, useMemo, useState } from 'react';

import { BanIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import {
  PopoverComboboxContent,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
} from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useDeskId } from '@/hooks/useDeskId';
import { AgentIcon } from '@/modules/humanChat/components/AgentIcon/AgentIcon';
import { useConversationMutations } from '@/modules/humanChat/hooks/useConversations';
import { selectAllAgents } from '@/redux/presence/selectors';
import { Presence } from '@/shared/types/presence';

import { AGENT_STATUS_PRIORITY } from '../../utils';

interface AssigneeState {
  sessionId: string;
  assigneeAgentId: string;
  assigneeBrainId: string;
}

/**
 * Rendered only while the popover is open so the agent list and its Redux
 * subscription don't cost anything on closed triggers.
 */
const AssigneePopoverContent = ({
  sessionId,
  assigneeAgentId,
  assigneeBrainId,
  align,
  onClose,
}: AssigneeState & { align: 'start' | 'center' | 'end'; onClose: () => void }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const deskId = useDeskId();
  const agents = useSelector(selectAllAgents);
  const { updateConversation } = useConversationMutations({ deskId, sessionId });

  const filteredAgents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return [...(agents ?? [])]
      .filter((agent) => !term || agent.name?.toLowerCase().includes(term))
      .sort((a, b) => {
        if (a.agent_id === assigneeAgentId) {
          return -1;
        }
        if (b.agent_id === assigneeAgentId) {
          return 1;
        }
        return (AGENT_STATUS_PRIORITY[a.status] ?? 4) - (AGENT_STATUS_PRIORITY[b.status] ?? 4);
      });
  }, [agents, searchTerm, assigneeAgentId]);

  const handleSelect = useCallback(
    (agent: Presence | null) => {
      updateConversation({ assignee_agent_id: agent?.agent_id ?? null, assignee_brain_id: null });
      onClose();
    },
    [updateConversation, onClose]
  );

  return (
    <PopoverComboboxContent align={align} className="w-58">
      <PopoverComboboxInput
        value={searchTerm}
        onValueChange={setSearchTerm}
        placeholder="departments.assign_an_agent"
      />
      <PopoverComboboxList>
        {/* Unassign is a fixed action, pinned above the search results rather than
            filtered alongside them, so the empty state below reads correctly. */}
        {Boolean(assigneeAgentId || assigneeBrainId) && (
          <>
            <PopoverComboboxGroup>
              <PopoverComboboxItem value="__unassign__" onSelect={() => handleSelect(null)}>
                <BanIcon size={16} className="mr-2" />
                {t('conversation.unassign')}
              </PopoverComboboxItem>
            </PopoverComboboxGroup>
            <Separator />
          </>
        )}
        {filteredAgents.length > 0 ? (
          <PopoverComboboxGroup>
            {filteredAgents.map((agent) => (
              <PopoverComboboxItem
                key={agent.agent_id}
                value={agent.agent_id}
                onSelect={() => handleSelect(agent)}
                selected={agent.agent_id === assigneeAgentId}
                className="flex items-center gap-2"
              >
                <AgentIcon agent={agent} />
                <TruncatedText text={agent.name || t('common.unknown')} />
              </PopoverComboboxItem>
            ))}
          </PopoverComboboxGroup>
        ) : (
          <div className="px-2 py-3 text-center text-sm text-foreground-muted">
            {t('departments.no_agents')}
          </div>
        )}
      </PopoverComboboxList>
    </PopoverComboboxContent>
  );
};

export interface AssigneePopoverProps extends AssigneeState {
  /** Trigger element; receives the Radix popover props via `asChild`. */
  trigger: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  /** When set, the trigger gets a tooltip that hides while the popover is open. */
  tooltip?: string;
}

/**
 * Agent picker for a conversation's assignee. Shared by the conversation list
 * and the right sidebar — only the trigger differs between the two.
 */
export const AssigneePopover = ({
  sessionId,
  assigneeAgentId,
  assigneeBrainId,
  trigger,
  open,
  onOpenChange,
  align = 'end',
  tooltip,
}: AssigneePopoverProps) => {
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const popoverTrigger = <PopoverTrigger asChild>{trigger}</PopoverTrigger>;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {tooltip ? (
        <InfoTooltip content={tooltip} open={open ? false : undefined} showArrow>
          {popoverTrigger}
        </InfoTooltip>
      ) : (
        popoverTrigger
      )}
      {open && (
        <AssigneePopoverContent
          sessionId={sessionId}
          assigneeAgentId={assigneeAgentId}
          assigneeBrainId={assigneeBrainId}
          align={align}
          onClose={handleClose}
        />
      )}
    </Popover>
  );
};
